import { type ChangeEvent, type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { type SelectOptionDefinition } from '@mui/base/useSelect/useSelect.types';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { InputAdornmentComponent } from '@vakantiesnl/components/src/atoms/InputAdornment';
import { CheckboxFormField } from '@vakantiesnl/components/src/molecules/CheckboxFormField';
import { MobileFilterMenuWrapper } from '@vakantiesnl/components/src/molecules/MobileFilterMenuWrapper';
import {
	type SelectFormFieldProps,
	type SelectFormFieldRenderValue,
} from '@vakantiesnl/components/src/molecules/SelectFormField';
import { SelectFormFieldMultiRoom } from '@vakantiesnl/components/src/molecules/SelectFormField/MultiRoom/SelectFormFieldMultiRoom';
import { SelectFormField } from '@vakantiesnl/components/src/molecules/SelectFormField/OldSelectFormField';
import { type TextfieldComponentProps } from '@vakantiesnl/components/src/molecules/TextField';
import { getConsecutiveDayRanges } from '@vakantiesnl/components/src/utils/consecutiveDayRanges';
import { useParseDurationsFilterConfig } from '@vakantiesnl/services/src/hooks/filter-hooks';
import { DURATION_CONFIG_OPTIONS } from '@vakantiesnl/services/src/util/durations';
import { type MicroCopy, type Search } from '@vakantiesnl/types';
import { noop, parseInt, isEqual } from 'lodash';

import { useCustomStyles } from './TripFilterMenu.style';
import { SelectFormFieldCalendar } from '../SelectFormField/Calendar/SelectFormFieldCalendar';

export type TripFilterMenuProps = {
	microCopies: MicroCopy;
	partyString: string;
	airportOptions: SelectOptionDefinition<string>[] | null;
	mealplanOptions: SelectOptionDefinition<string>[] | null;
	onSetDepartureDate: (departureDate: string[]) => void;
	onChangeAirport: SelectFormFieldProps<string, true>['onChange'];
	onChangeDurationRange: SelectFormFieldProps<string, true>['onChange'];
	onPartyUpdate: (party: Search.PartyComposition[]) => void;
	onClosePartyPicker: (party: Search.PartyComposition[]) => void;
	onChangeMealplan: SelectFormFieldProps<string, true>['onChange'];
	durations: number[];
	departureDate?: string[] | null;
	airports?: string[];
	mealplans?: string[];
	party?: Search.PartyComposition[];
	isOwnTransport?: boolean;
	withSelectableTransfer?: boolean;
	onWithSelectableTransfer?: (e: ChangeEvent<HTMLInputElement>) => void;
};

// Defaults to one, since the pax filter is always selected.
const DEFAULT_FILTER_COUNT = 1;

export const TripFilterMenu: FC<TripFilterMenuProps> = ({
	microCopies,
	partyString,
	airportOptions,
	mealplanOptions,
	onSetDepartureDate,
	onChangeAirport,
	onChangeDurationRange,
	onPartyUpdate,
	onClosePartyPicker,
	onChangeMealplan,
	departureDate,
	airports,
	durations,
	mealplans,
	party,
	isOwnTransport,
	withSelectableTransfer,
	onWithSelectableTransfer,
}) => {
	const { classes } = useCustomStyles();
	const allRanges = useParseDurationsFilterConfig(microCopies);
	const flattenRanges: string[] = allRanges.reduce(
		(prev: string[], option) => [...prev, ...option.value.split(',')],
		[],
	);

	const durationDaysOptions = flattenRanges
		? flattenRanges.map((day: string) => ({
				label: `${day} ${microCopies['common.days']}`,
				value: day,
			}))
		: [];

	const durationsSelectedRender: SelectFormFieldRenderValue<true> = useCallback(
		(selectedDurations: string[] | null) => {
			if (selectedDurations?.length) {
				const durations = (selectedDurations || []).map((item) => parseInt(item)).sort((a, b) => a - b);
				return `${getConsecutiveDayRanges(durations).join('; ')} ${microCopies['common.days']}`;
			} else {
				return microCopies['filters.noPreference'];
			}
		},
		[microCopies],
	);

	const [filterCount, setFilterCount] = useState(DEFAULT_FILTER_COUNT);

	const onCloseMobileFilterMenuWrapper = useCallback(() => {
		let numberOfFiltersSelected = DEFAULT_FILTER_COUNT;

		if (departureDate) {
			numberOfFiltersSelected++;
		}

		if (durations && durations.length > 0 && !isEqual(durations, DURATION_CONFIG_OPTIONS.default.array)) {
			numberOfFiltersSelected++;
		}

		if (airports && airports.length > 0) {
			numberOfFiltersSelected++;
		}

		if (mealplans && mealplans.length > 0) {
			numberOfFiltersSelected++;
		}

		if (withSelectableTransfer) {
			numberOfFiltersSelected++;
		}

		setFilterCount(numberOfFiltersSelected);
	}, [departureDate, durations, airports, mealplans, withSelectableTransfer]);

	const durationsInputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="time" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	const airportsInputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="airplane" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	const mealplansInputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="food" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	useEffect(() => {
		// Call initially to update when the page is loaded
		onCloseMobileFilterMenuWrapper();
	});

	return (
		<MobileFilterMenuWrapper
			microCopies={microCopies}
			drawerTitle={microCopies['filters.tripfilters.drawerTitle']}
			numberOfActiveFilters={filterCount}
			onClose={onCloseMobileFilterMenuWrapper}
			openButtonText={microCopies['common.filters']}
		>
			<div className={classes.filterMenuWrapper}>
				<div className={classes.filterMenu}>
					<SelectFormFieldCalendar
						departureDate={departureDate || null}
						setDepartureDate={onSetDepartureDate}
						setFlexDays={noop}
						flexDays={0}
						hasFlexibilityOptions={false}
						hasDrawerOnMobileTablet={true}
						inputLabel={microCopies['filters.departureDateLabelText']}
						dataCy="tripFilterDatePicker"
					/>
					<SelectFormField
						microCopies={microCopies}
						placeholder={microCopies['filters.noPreference']}
						label={microCopies['common.duration']}
						options={durationDaysOptions}
						onChange={onChangeDurationRange}
						value={durations.map(String)}
						variant="checkbox"
						multiple={true}
						InputProps={durationsInputProps}
						customRenderValue={durationsSelectedRender}
					/>
					{!!airportOptions?.length && airportOptions?.length > 0 && (
						<SelectFormField
							microCopies={microCopies}
							placeholder={microCopies['filters.noPreference']}
							label={microCopies['filters.departureAirport']}
							options={airportOptions}
							onChange={onChangeAirport}
							defaultValue={airports}
							variant="checkbox"
							multiple={true}
							InputProps={airportsInputProps}
						/>
					)}
					<SelectFormFieldMultiRoom
						label={microCopies['filters.partyComposition']}
						defaultValue={partyString}
						initialParty={party}
						onChange={onPartyUpdate}
						onClose={onClosePartyPicker}
						hideMultiRoom={isOwnTransport}
					/>
					{!!mealplanOptions?.length && mealplanOptions?.length > 0 && (
						<SelectFormField
							microCopies={microCopies}
							placeholder={microCopies['filters.noPreference']}
							label={microCopies['common.mealplan']}
							defaultValue={mealplans}
							options={mealplanOptions}
							onChange={onChangeMealplan}
							variant="checkbox"
							multiple={true}
							InputProps={mealplansInputProps}
						/>
					)}
				</div>
				{!isOwnTransport && (
					<div className={classes.filterOptions}>
						<CheckboxFormField
							label={
								<>
									<Icon type="transfer" /> {microCopies['transfer.inclusive']}
								</>
							}
							checked={withSelectableTransfer}
							data-cy="selectableTransferFilter"
							onChange={onWithSelectableTransfer}
						/>
					</div>
				)}
			</div>
		</MobileFilterMenuWrapper>
	);
};
